import { computed, ref } from 'vue';
import { api } from '../services/api';
import { localSync } from '../services/LocalSyncService';
import type { WebUser } from '../types';

const currentUser = ref<WebUser | null>(api.getSavedUser());
const isAuthChecking = ref<boolean>(false);

export function useAuth() {
  const isLoggedIn = computed(() => Boolean(currentUser.value));

  const setUser = (user: WebUser, token?: string) => {
    currentUser.value = user;
    api.saveUser(user);
    if (token) {
      api.setToken(token);
    }
  };

  const logout = () => {
    localSync.stop();
    currentUser.value = null;
    api.clearToken();
  };

  const checkAuth = async (): Promise<boolean> => {
    const token = api.getToken();
    const savedUser = api.getSavedUser();
    if (!token) {
      currentUser.value = null;
      return false;
    }

    if (savedUser) {
      currentUser.value = savedUser;
    }

    // Offline-first: if browser is offline and we have a cached user + token, allow immediate offline access
    if (typeof navigator !== 'undefined' && !navigator.onLine && savedUser) {
      localSync.state.connection = 'offline';
      return true;
    }

    isAuthChecking.value = true;
    try {
      const state = await api.getSessionState();
      setUser(state.user);
      return true;
    } catch (e: any) {
      console.warn('Auth check warning:', e);
      // Only log out on explicit 401 Unauthorized or if no cached user exists
      if (e?.status === 401 || !api.getToken() || !savedUser) {
        logout();
        return false;
      }
      // Network unreachable while user has valid cached session -> stay logged in for offline study
      currentUser.value = savedUser;
      return true;
    } finally {
      isAuthChecking.value = false;
    }
  };

  return {
    currentUser,
    isLoggedIn,
    isAuthChecking,
    setUser,
    logout,
    checkAuth,
  };
}
